import { useCallback } from "react";

/**
 * Scrolls a section of the *current* page clear of the fixed navbar.
 *
 * `.navbar` is `position: fixed` at the top of the viewport, so any scroll that
 * aligns a section's top edge with the viewport's parks that section's heading
 * underneath it — 87px of it at 1920x942, where the navbar measures 192px and
 * the section's own padding only lifts its heading 105px.
 *
 * Two things make CSS insufficient here, both measured rather than assumed:
 *
 *  - `scroll-margin-top` on the target has no effect, because the Webflow
 *    interaction engine (`window._Agapae`) intercepts same-page anchor clicks
 *    and animates the scroll itself via `window.scrollTo`. Scroll margin is
 *    honoured by native fragment navigation and `scrollIntoView`, neither of
 *    which is what runs here. Injecting `scroll-margin-top: 220px` moved the
 *    landing position by exactly 0px.
 *  - The navbar's height is breakpoint-dependent (192px above 991px, 173px at
 *    991px, 60px at 767px and below), so a hard-coded offset would be wrong at
 *    two of the three sizes.
 *
 * So the offset is measured at click time — the same arithmetic Navbar and
 * Footer already use for the home page's sections, kept in one place.
 *
 * @param {string} targetId  id of a section in the current document
 */
export const scrollToSection = (targetId) => {
  const element = document.getElementById(targetId);
  if (!element) return;

  const navbar = document.querySelector(".navbar");

  // Measured in the next frame: callers may have just navigated or re-rendered,
  // and `offsetHeight` read in the same frame can still see a pre-layout navbar.
  requestAnimationFrame(() => {
    const navbarHeight = navbar ? navbar.offsetHeight : 160;
    const buffer = 20;
    const elementPosition =
      element.getBoundingClientRect().top + window.scrollY;

    window.scrollTo({
      top: elementPosition - navbarHeight - buffer,
      behavior: "smooth",
    });
  });
};

/**
 * Click handler for a link pointing at a section of the page it sits on.
 *
 * Both `preventDefault` and `stopPropagation` are required: the first cancels
 * the native fragment jump, the second keeps the click from reaching the
 * Webflow engine's document-level listener, which would otherwise animate to
 * the unadjusted position on top of us.
 *
 * The link keeps its real `href="#id"`, so it stays a crawlable,
 * middle-clickable anchor — and identical to the one the pre-rendered static
 * document ships.
 *
 * @param {string} targetId  id of a section in the current document
 * @returns {(e: MouseEvent) => void} onClick handler
 */
const useScrollToSection = (targetId) =>
  useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      scrollToSection(targetId);
    },
    [targetId],
  );

export default useScrollToSection;
